/* eslint-disable import/no-anonymous-default-export */
// Firebase Migration Runner
// This script performs the actual migration from collections + arrays to subcollections

import { collection, doc, getDoc, setDoc, writeBatch, query, orderBy, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { initializeUserSubcollections, migrateUserDataToSubcollections, saveUserTestResult, saveUserNote, saveUserDoubt, saveUserProgress, saveUserActivity, enrollUserInCourse } from '../firebase/subcollections';

// Utility function to generate unique IDs
const generateId = () => {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

// Migration status tracking
let migrationStatus = {
  totalUsers: 0,
  processedUsers: 0,
  migratedTestResults: 0,
  migratedNotes: 0,
  migratedDoubts: 0,
  migratedProgress: 0,
  migratedActivities: 0,
  migratedCourses: 0,
  errors: []
};

// Get all users from Firebase
export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: users };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get test results for a user from old structure
export const getOldTestResults = async (userId) => {
  try {
    // Try with composite index first
    const q = query(
      collection(db, 'testResults'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const testResults = [];
    snapshot.forEach((doc) => {
      testResults.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: testResults };
  } catch (error) {
    try {
      // Fallback to simple query without ordering
      const q = query(
        collection(db, 'testResults'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      const testResults = [];
      snapshot.forEach((doc) => {
        testResults.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory
      testResults.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return dateB - dateA;
      });
      return { success: true, data: testResults };
    } catch (fallbackError) {
      return { success: false, error: fallbackError.message };
    }
  }
};

// Get notes for a user from old structure
export const getOldNotes = async (userId) => {
  try {
    // Try with composite index first
    const q = query(
      collection(db, 'notes'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const notes = [];
    snapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: notes };
  } catch (error) {
    try {
      // Fallback to simple query without ordering
      const q = query(
        collection(db, 'notes'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      const notes = [];
      snapshot.forEach((doc) => {
        notes.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory
      notes.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return dateB - dateA;
      });
      return { success: true, data: notes };
    } catch (fallbackError) {
      return { success: false, error: fallbackError.message };
    }
  }
};

// Get doubts for a user from old structure
export const getOldDoubts = async (userId) => {
  try {
    // Try with composite index first
    const q = query(
      collection(db, 'doubts'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const doubts = [];
    snapshot.forEach((doc) => {
      doubts.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: doubts };
  } catch (error) {
    try {
      // Fallback to simple query without ordering
      const q = query(
        collection(db, 'doubts'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      const doubts = [];
      snapshot.forEach((doc) => {
        doubts.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory
      doubts.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return dateB - dateA;
      });
      return { success: true, data: doubts };
    } catch (fallbackError) {
      return { success: false, error: fallbackError.message };
    }
  }
};

// Get user progress from old structure
export const getOldUserProgress = async (userId) => {
  try {
    // Try with composite index first
    const q = query(
      collection(db, 'userProgress'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const progress = [];
    snapshot.forEach((doc) => {
      progress.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: progress };
  } catch (error) {
    try {
      // Fallback to simple query without ordering
      const q = query(
        collection(db, 'userProgress'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      const progress = [];
      snapshot.forEach((doc) => {
        progress.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory
      progress.sort((a, b) => {
        const dateA = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
        const dateB = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
        return dateB - dateA;
      });
      return { success: true, data: progress };
    } catch (fallbackError) {
      return { success: false, error: fallbackError.message };
    }
  }
};

// Get user activities from old structure
export const getOldUserActivities = async (userId) => {
  try {
    // Try with composite index first
    const q = query(
      collection(db, 'userActivities'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    const activities = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.lastActivity) {
        activities.push({
          id: doc.id,
          type: 'progress_update',
          title: `Progress updated in ${data.examId || 'exam'}`,
          description: `Score: ${data.score || 0}%`,
          timestamp: data.updatedAt || data.createdAt
        });
      }
    });
    return { success: true, data: activities };
  } catch (error) {
    try {
      // Fallback to simple query without ordering
      const q = query(
        collection(db, 'userActivities'),
        where('userId', '==', userId),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const activities = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.lastActivity) {
          activities.push({
            id: doc.id,
            type: 'progress_update',
            title: `Progress updated in ${data.examId || 'exam'}`,
            description: `Score: ${data.score || 0}%`,
            timestamp: data.updatedAt || data.createdAt
          });
        }
      });
      // Sort in memory
      activities.sort((a, b) => {
        const dateA = a.timestamp?.toDate?.() || a.timestamp || new Date(0);
        const dateB = b.timestamp?.toDate?.() || b.timestamp || new Date(0);
        return dateB - dateA;
      });
      return { success: true, data: activities };
    } catch (fallbackError) {
      return { success: false, error: fallbackError.message };
    }
  }
};

// Get user course data from old structure
export const getOldUserCourses = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return { success: true, data: userData.courses || [] };
    }
    return { success: true, data: [] };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Migrate a single user's data
export const migrateSingleUser = async (userId) => {
  try {
    // Step 1: Initialize subcollections
    await initializeUserSubcollections(userId);
    // Step 2: Get old data
    const [
      oldTestResults,
      oldNotes,
      oldDoubts,
      oldProgress,
      oldActivities,
      oldCourses
    ] = await Promise.all([
      getOldTestResults(userId),
      getOldNotes(userId),
      getOldDoubts(userId),
      getOldUserProgress(userId),
      getOldUserActivities(userId),
      getOldUserCourses(userId)
    ]);
    
    // Step 3: Migrate test results
    if (oldTestResults.success && oldTestResults.data.length > 0) {
      for (const testResult of oldTestResults.data) {
        await saveUserTestResult(userId, {
          testId: testResult.testId || testResult.id,
          examId: testResult.examId,
          score: testResult.score,
          totalQuestions: testResult.totalQuestions,
          correctAnswers: testResult.correctAnswers,
          timeTaken: testResult.timeTaken,
          accuracy: testResult.accuracy,
          completedAt: testResult.completedAt || testResult.createdAt
        });
        migrationStatus.migratedTestResults++;
      }
      }
    
    // Step 4: Migrate notes
    if (oldNotes.success && oldNotes.data.length > 0) {
      for (const note of oldNotes.data) {
        await saveUserNote(userId, {
          title: note.title,
          content: note.content,
          subject: note.subject,
          topic: note.topic,
          tags: note.tags || []
        });
        migrationStatus.migratedNotes++;
      }
      }
    
    // Step 5: Migrate doubts
    if (oldDoubts.success && oldDoubts.data.length > 0) {
      for (const doubt of oldDoubts.data) {
        await saveUserDoubt(userId, {
          question: doubt.question,
          subject: doubt.subject,
          topic: doubt.topic,
          priority: doubt.priority || 'medium',
          status: doubt.status || 'pending'
        });
        migrationStatus.migratedDoubts++;
      }
      }
    
    // Step 6: Migrate progress
    if (oldProgress.success && oldProgress.data.length > 0) {
      for (const progress of oldProgress.data) {
        await saveUserProgress(userId, {
          examId: progress.examId,
          subject: progress.subject,
          topic: progress.topic,
          score: progress.score,
          totalQuestions: progress.totalQuestions,
          correctAnswers: progress.correctAnswers,
          timeSpent: progress.timeSpent,
          accuracy: progress.accuracy
        });
        migrationStatus.migratedProgress++;
      }
      }
    
    // Step 7: Migrate activities
    if (oldActivities.success && oldActivities.data.length > 0) {
      for (const activity of oldActivities.data) {
        await saveUserActivity(userId, {
          type: activity.type,
          title: activity.title,
          description: activity.description,
          metadata: activity.metadata || {}
        });
        migrationStatus.migratedActivities++;
      }
      }
    
    // Step 8: Migrate courses
    if (oldCourses.success && oldCourses.data.length > 0) {
      for (const course of oldCourses.data) {
        await enrollUserInCourse(userId, course.courseId || course.id, {
          courseName: course.courseName || course.title,
          instructor: course.instructor,
          progress: course.progress || 0,
          totalLessons: course.totalLessons || 0,
          completedLessons: course.completedLessons || 0
        });
        migrationStatus.migratedCourses++;
      }
      }
    
    migrationStatus.processedUsers++;
    return { success: true, userId };
  } catch (error) {
    migrationStatus.errors.push({ userId, error: error.message });
    return { success: false, userId, error: error.message };
  }
};

// Run full migration for all users
export const runFullMigration = async () => {
  try {
    // Reset migration status
    migrationStatus = {
      totalUsers: 0,
      processedUsers: 0,
      migratedTestResults: 0,
      migratedNotes: 0,
      migratedDoubts: 0,
      migratedProgress: 0,
      migratedActivities: 0,
      migratedCourses: 0,
      errors: []
    };
    
    // Get all users
    const usersResult = await getAllUsers();
    if (!usersResult.success) {
      throw new Error('Failed to get users');
    }
    
    migrationStatus.totalUsers = usersResult.data.length;
    // Migrate each user
    for (const user of usersResult.data) {
      await migrateSingleUser(user.id);
      
      // Add a small delay to avoid overwhelming Firebase
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return { success: true, status: migrationStatus };
  } catch (error) {
    return { success: false, status: migrationStatus, error: error.message };
  }
};

// Run migration for a specific user
export const runUserMigration = async (userId) => {
  try {
    const result = await migrateSingleUser(userId);
    
    if (result.success) {
      } else {
      }
    
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get migration status
export const getMigrationStatus = () => {
  return migrationStatus;
};

// Create sample data for testing migration
export const createSampleData = async (userId) => {
  try {
    // Initialize subcollections
    await initializeUserSubcollections(userId);
    
    // Create sample test result
    await saveUserTestResult(userId, {
      testId: 'sample-test-1',
      examId: 'cat',
      score: 85,
      totalQuestions: 100,
      correctAnswers: 85,
      timeTaken: 7200,
      accuracy: 85,
      completedAt: new Date()
    });
    
    // Create sample note
    await saveUserNote(userId, {
      title: 'Sample Math Notes',
      content: 'This is a sample note for testing migration.',
      subject: 'Mathematics',
      topic: 'Algebra',
      tags: ['math', 'algebra', 'sample']
    });
    
    // Create sample doubt
    await saveUserDoubt(userId, {
      question: 'How do I solve quadratic equations?',
      subject: 'Mathematics',
      topic: 'Algebra',
      priority: 'high',
      status: 'pending'
    });
    
    // Create sample progress
    await saveUserProgress(userId, {
      examId: 'cat',
      subject: 'Quantitative Aptitude',
      topic: 'Algebra',
      score: 80,
      totalQuestions: 20,
      correctAnswers: 16,
      timeSpent: 1800,
      accuracy: 80
    });
    
    // Create sample activity
    await saveUserActivity(userId, {
      type: 'test_completion',
      title: 'Completed CAT Mock Test',
      description: 'Scored 85% in Quantitative Aptitude section',
      metadata: { examId: 'cat', section: 'quantitative' }
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Verify migration results
export const verifyMigration = async (userId) => {
  try {
    const { 
      getUserTestResults, 
      getUserNotes, 
      getUserDoubts, 
      getUserProgress, 
      getUserActivities,
      getUserEnrollments 
    } = await import('../firebase/subcollections');
    
    const [
      testResults,
      notes,
      doubts,
      progress,
      activities,
      enrollments
    ] = await Promise.all([
      getUserTestResults(userId, 10),
      getUserNotes(userId, 10),
      getUserDoubts(userId, 10),
      getUserProgress(userId, 10),
      getUserActivities(userId, 10),
      getUserEnrollments(userId)
    ]);
    
    const verification = {
      testResults: testResults.success ? testResults.data.length : 0,
      notes: notes.success ? notes.data.length : 0,
      doubts: doubts.success ? doubts.data.length : 0,
      progress: progress.success ? progress.data.length : 0,
      activities: activities.success ? activities.data.length : 0,
      enrollments: enrollments.success ? enrollments.data.length : 0
    };
    
    return { success: true, data: verification };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default {
  runFullMigration,
  runUserMigration,
  getMigrationStatus,
  createSampleData,
  verifyMigration
};
